import { styles } from "./extensionDetails.styles";
import { useParams } from "react-router-dom";

import { Icon, Link, Spinner, SpinnerSize, Stack, Text } from "@fluentui/react";
import { useFetchExperiments } from "../../Hooks/AdoHooks/UseFetchExperiments";
import { useEv2ExperimentParams } from "../../Hooks/UseCurrentExtension";
import { notSetCell } from "../../Shared/TableRenderers";
import { REPO_URL } from "../../Shared/shared.consts";
import useAppInsights from "../../Hooks/UseAppInsights";
import { NavType } from "../../Shared/AppInsights";

interface ExtensionDetailsOverviewProps {
  /**
   * Optional message to display in the overview. Will display messages at the top of the overview.
   */
  overviewMessage?: React.ReactNode;
}

/**
 * Component that displays an overview of the extension details, including extension name, number of experiments, and number of archived experiments.
 * Also includes a link to the repository configurations for the extension. Used in the ExtensionDetails component.
 */
const ExtensionDetailsOverview: React.FC<ExtensionDetailsOverviewProps> = ({
  overviewMessage,
}) => {
  const { extensionFolder } = useEv2ExperimentParams();
  const { extensionName } = useParams<{ extensionName: string }>();
  const { trackNavigationEvent } = useAppInsights("ExtensionDetailsOverview");

  const {
    value: { experiments = [], archivedExperiments = [] } = {
      experiments: [],
      archivedExperiments: [],
    },
    loading: loadingExperiments,
  } = useFetchExperiments();

  return (
    <Stack horizontalAlign="start" className={styles.extensionDetailsOverview}>
      {overviewMessage}
      <Stack
        horizontal
        verticalAlign="start"
        horizontalAlign="space-between"
        style={{ width: "100%" }}
      >
        <Stack
          tokens={{ childrenGap: 5 }}
          className={styles.extensionDetailsOverviewItems}
        >
          <Stack.Item align="start">
            <Text className={styles.extensionDetailsOverviewTextKey}>
              Extension name:{" "}
            </Text>
            <span>{extensionName}</span>
          </Stack.Item>
          <Stack.Item align="start">
            <Text className={styles.extensionDetailsOverviewTextKey}>
              Experiments:{" "}
            </Text>
            <span>
              {loadingExperiments ? (
                <Spinner
                  size={SpinnerSize.xSmall}
                  styles={{ root: styles.inlineSpinner }}
                />
              ) : (
                experiments.length || notSetCell("No experiments")
              )}
            </span>
          </Stack.Item>
          <Stack.Item align="start">
            <Text className={styles.extensionDetailsOverviewTextKey}>
              Archived experiments:{" "}
            </Text>
            <span>
              {loadingExperiments ? (
                <Spinner
                  size={SpinnerSize.xSmall}
                  styles={{ root: styles.inlineSpinner }}
                />
              ) : (
                archivedExperiments.length ||
                notSetCell("No archived experiments")
              )}
            </span>
          </Stack.Item>
        </Stack>
        <Link
          href={`${REPO_URL}?path=/Extensions/${extensionFolder?.extensionRepoPath}`}
          target="_blank"
          onClick={() => {
            trackNavigationEvent({
              destination: `${REPO_URL}?path=/Extensions/${extensionFolder?.extensionRepoPath}`,
              navType: NavType.External,
            });
          }}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
            <span>Repository configurations</span>
            <Icon iconName="NavigateExternalInline" />
          </Stack>
        </Link>
      </Stack>
    </Stack>
  );
};

export default ExtensionDetailsOverview;
